import { DateTime } from 'luxon';
import { leewayWindowCloses, leewayWindowOpens } from '../../../scheduling/schedule.after.service';
import { ScheduleLeewayWindow } from '../../../scheduling/schedule.rule';

/**
 * The local dates a Visit may be done in (docs/plans/visit-materialisation-horizon.md section 15).
 *
 * A Visit with no leeway task has `windowStart === windowEnd === dueDate`, so every window-aware read
 * behaves exactly as the plain due-date read did.
 */
export interface VisitComplianceWindow {
    windowStart: string;
    windowEnd: string;
}

export enum VisitWindowPlacement {
    Inside = 'Inside',
    /** Before `windowStart`: the service does not count towards compliance. */
    Early = 'Early',
    /** After `windowEnd`: a compliance breach. */
    Late = 'Late',
}

/**
 * Union over the Visit's tasks of `[due − early, due + late]`; tasks without leeway pin the due date itself.
 *
 * `holidays` are the line's honoured public-holiday dates; without them a business-day side skips weekends only.
 */
export function visitComplianceWindow(dueDate: string, schedules: (ScheduleLeewayWindow | undefined)[], holidays?: readonly string[]): VisitComplianceWindow {

    let windowStart = dueDate;
    let windowEnd = dueDate;
    for (const leeway of schedules) {
        const from = leewayWindowOpens(leeway, dueDate, holidays);
        const until = leewayWindowCloses(leeway, dueDate, holidays);
        if (from < windowStart) windowStart = from;
        if (until > windowEnd) windowEnd = until;
    }

    return { windowStart, windowEnd };
}

export function hasVisitComplianceWindow(window: Partial<VisitComplianceWindow> & { localDate: string }): boolean {

    return (!!window.windowStart && window.windowStart !== window.localDate) || (!!window.windowEnd && window.windowEnd !== window.localDate);
}

/** Inclusive local-date overlap; `periodEnd` is the last day IN the period. */
export function visitWindowOverlaps(window: VisitComplianceWindow, periodStart: string, periodEnd: string): boolean {

    return window.windowStart <= periodEnd && window.windowEnd >= periodStart;
}

export function visitWindowPlacement(window: Partial<VisitComplianceWindow> & { localDate: string }, date: string): VisitWindowPlacement {

    if (date < (window.windowStart ?? window.localDate)) return VisitWindowPlacement.Early;
    if (date > (window.windowEnd ?? window.localDate)) return VisitWindowPlacement.Late;
    return VisitWindowPlacement.Inside;
}

type VisitWindowSubject = Partial<VisitComplianceWindow> & { localDate?: string };

const dayLabel = (date: string) => DateTime.fromISO(date).toFormat('d LLL');

/** Row/detail label, "due 15 Dec · can be done from 15 Nov"; undefined for a Visit with no leeway. */
export function visitWindowFromLabel(window: VisitWindowSubject): string | undefined {

    if (!window.localDate || !hasVisitComplianceWindow({ ...window, localDate: window.localDate })) return undefined;
    return `due ${dayLabel(window.localDate)} · can be done from ${dayLabel(window.windowStart ?? window.localDate)}`;
}

/** Scheduler card label, "due 15 Dec · 15 Nov–15 Jan"; undefined for a Visit with no leeway. */
export function visitWindowRangeLabel(window: VisitWindowSubject): string | undefined {

    if (!window.localDate || !hasVisitComplianceWindow({ ...window, localDate: window.localDate })) return undefined;
    return `due ${dayLabel(window.localDate)} · ${dayLabel(window.windowStart ?? window.localDate)}–${dayLabel(window.windowEnd ?? window.localDate)}`;
}

/** The reschedule warning for `startDate`, or undefined inside the window or on a Visit with none. */
export function visitWindowWarning(row: VisitWindowSubject & { localDate: string; timezone?: string }, startDate: Date): string | undefined {

    if (!hasVisitComplianceWindow(row)) return undefined;
    const date = DateTime.fromJSDate(new Date(startDate), { zone: row.timezone || 'UTC' }).toISODate()!;
    const day = (iso: string | undefined) => DateTime.fromISO(iso ?? row.localDate).toFormat('d LLL yyyy');
    switch (visitWindowPlacement(row, date)) {
        case VisitWindowPlacement.Early:
            return `The compliance window opens on ${day(row.windowStart)}. A service before then does not count.`;
        case VisitWindowPlacement.Late:
            return `The compliance window closes on ${day(row.windowEnd)}. A service after then is a compliance breach.`;
        default:
            return undefined;
    }
}
